/*eslint-disable*/
import React, { useEffect, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Menu,
  MenuItem,
  Select,
  FormControl,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import  Pagination  from '../Pagination/Pagination';
import {
  hoverRowStyles,
  tableCellStyleAdmin,
  tableRowCellAdmin,
} from './tableStyles';
import NoRecordsFound from '../NoRecordsFound';
import DeleteModal from '../modal/deleteModal';
import { menuItemStyles, menuStyles } from '../../styles/menu';
import { StatusButton } from '../../styles/StatusButton';
import { iconStyles } from '../../styles/iconStyles';

function CustomDropdownIcon(props: any) {
  return (
    <KeyboardArrowDownIcon
      {...props}
      sx={{
        ...iconStyles,
        transition: 'transform 0.3s ease',
      }}
    />
  );
}

interface CommonTableProps {
  data: any[];
  columns: Array<{
    field: string;
    headerName: string;
    align?: 'left' | 'right';
  }>;
  styles: any;
  pages: string;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
}

const ActionsTable = ({
  data,
  columns,
  styles,
  onEdit,
  onDelete,
  pages = 'Addtional Admin',
}: CommonTableProps) =>{
  const [page, setPage] = useState(0);
  const isEmptyRecords = data.length === 0;
  const rowsPerPage = 9;
  const pageCount = Math.ceil(data.length / rowsPerPage);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [currentRowId, setCurrentRowId] = useState<string | number | null>(
    null,
  );
  const [openDialog, setOpenDialog] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<string>('');
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  const handleOpenDialog = (id: string) => {
    setRowToDelete(id);
    setOpenDialog(true);
  };
  const handlePermissionChange = (event: any, rowId: string | number) => {
    console.log(`Permission updated for row ${rowId}:`, event.target.value);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, id: string) => {
    setAnchorEl(event.currentTarget);
    setCurrentRowId(id);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setRowToDelete('');
  };
  const handleConfirmRevoke = () => {
    if (rowToDelete && onDelete) {
      onDelete(rowToDelete);
    }
    handleCloseDialog();
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
    setCurrentRowId(null);
  };

  useEffect(() => {
    const newPageCount = Math.ceil(data.length / rowsPerPage);

    if (page >= newPageCount) {
      const newPage = Math.max(0, newPageCount - 1);
      setPage(newPage);
    }
  }, [data.length, page, rowsPerPage]);

  interface Action {
    label: string;
    action?: (id: string) => void;
  }

  interface ModalContent {
    modalHeading: string;
    modalMsg: string;
  }

  interface Permission {
    label: string;
    action?: () => void;
  }

  const actionsMapping: Record<string, Action[]> = {
    'User Queries': [
      { label: 'See All Details', action: onEdit },
      { label: 'Delete', action: handleOpenDialog },
    ],
    'Device Management': [
      { label: 'Change Access', action: onEdit },
      { label: 'Delete', action: handleOpenDialog },
    ],
    'Additional Admin': [
      { label: 'Revoke Access', action: onEdit },
      { label: 'Delete', action: handleOpenDialog },
    ],
    Default: [
      { label: 'Revoke Access', action: onEdit },
      { label: 'Delete', action: handleOpenDialog },
    ],
  };

  const modalContentMapping: Record<string, ModalContent> = {
    'User Queries': {
      modalHeading: 'Confirm User Query Deletion',
      modalMsg: 'Are you sure you want to delete the selected user query?',
    },
    'Device Management': {
      modalHeading: 'Confirm Device Management Deletion',
      modalMsg:
        'Are you sure you want to delete the selected user device management?',
    },
    'Additional Admin': {
      modalHeading: 'Confirm Action',
      modalMsg: 'Are you sure you want to proceed with this action?',
    },
    Default: {
      modalHeading: 'Confirm Action',
      modalMsg: 'Are you sure you want to proceed with this action?',
    },
  };

  const permissionsMapping: Record<string, Permission[]> = {
    'Additional Admin': [
      { label: 'Read' },
      { label: 'Write' },
      { label: 'Access Logs' },
      { label: 'All' },
    ],
    Default: [
      { label: 'Open' },
      { label: 'Resolved' },
      { label: 'In Progress' },
    ],
  };

  const actions = actionsMapping[pages] || actionsMapping.Default;
  const modalData = modalContentMapping[pages] || modalContentMapping.Default;
  const permissions = permissionsMapping[pages] || permissionsMapping.Default;
  return (
    <Box display="flex" flexDirection="column" flexGrow={1}>
      <TableContainer
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          scrollBehavior: 'smooth', 
          height: styles.height,
          borderBottom: '0.125rem solid lightgray',
        }}
      >
        <Table sx={{ border: 'none', boxShadow: 'none' }}>
          <TableHead
            sx={{
              position: 'sticky',
              top: 0,
              backgroundColor: 'white',
              zIndex: 1,
              boxShadow: '0px 2px 0px lightgray',
            }}
          >
            <TableRow>
              {columns?.map((column) => (
                <TableCell
                  key={column.field}
                  sx={tableCellStyleAdmin}
                  align={column.align || 'left'}
                >
                  {column.headerName}
                </TableCell>
              ))}
              <TableCell sx={tableCellStyleAdmin} align="right">
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isEmptyRecords && (
              <TableRow>
                <TableCell
                  colSpan={columns.length + 1}
                  align="center"
                  sx={{ borderBottom: 'none' }}
                >
                  <NoRecordsFound />
                </TableCell>
              </TableRow>
            )}
            {!isEmptyRecords &&
              data
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                ?.map((row: any) => (
                  <TableRow key={row.id || row._id} sx={hoverRowStyles}>
                    {columns?.map((column) => (
                      <TableCell key={column.field} sx={tableRowCellAdmin}>
                        {column.headerName === 'Permission' ? (
                          <FormControl fullWidth>
                            <Select
                              value={row[column.field] || 'Read'}
                              onChange={(event) =>
                                handlePermissionChange(event, row.id || row._id)
                              }
                              sx={{
                                border: 'none',
                                '& .MuiOutlinedInput-notchedOutline': {
                                  border: 'none',
                                },
                                fontSize: {
                                  xxl: '1rem',
                                  xl: '0.8rem',
                                  lg: '0.7rem',
                                  md: '0.6rem',
                                },
                                width: {
                                  xxl: '9rem',
                                  xl: '7rem',
                                  lg: '6rem',
                                  md: '5rem',
                                },
                              }}
                              MenuProps={{
                                PaperProps: {
                                  sx: {
                                    width: { xxl: '13rem', xl: '11rem' },
                                    paddingInline: '0.313rem',
                                  },
                                },
                              }}
                              IconComponent={CustomDropdownIcon}
                            >
                              {permissions.map(
                                ({ label, action: actionHandler }) =>
                                  actionHandler && (
                                    <MenuItem
                                      key={label}
                                      sx={menuItemStyles}
                                      onClick={() => {
                                        handleMenuClose();
                                        actionHandler();
                                      }}
                                    >
                                      {label}
                                    </MenuItem>
                                  ),
                              )}
                            </Select>
                          </FormControl>
                        ) : column.headerName === 'Status' ? (
                          <Box>
                            <StatusButton status={row.status} />
                          </Box>
                        ) : (
                          row[column.field] || 'N/A'
                        )}
                      </TableCell>
                    ))}
                    <TableCell align="right" sx={tableRowCellAdmin}>
                      <IconButton
                        onClick={(event) => handleMenuOpen(event, row)}
                      >
                        <MoreVertIcon sx={iconStyles} />
                      </IconButton>
                      <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl) && currentRowId === row}
                        onClose={handleMenuClose}
                        sx={menuStyles}
                      >
                        {actions.map(
                          ({ label, action }) =>
                            action && (
                              <MenuItem
                                key={label}
                                sx={menuItemStyles}
                                onClick={() => {
                                  handleMenuClose();
                                  action(row);
                                }}
                              >
                                {label}
                              </MenuItem>
                            ),
                        )}
                      </Menu>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box>
        {!isEmptyRecords && (
          <Box>
            <Pagination
              pageCount={pageCount}
              page={page}
              onPageChange={handleChangePage}
              data={data}
            />
          </Box>
        )}
      </Box>
      <DeleteModal
        open={openDialog}
        title={modalData.modalHeading}
        message={modalData.modalMsg}
        onConfirm={handleConfirmRevoke}
        onCancel={handleCloseDialog}
      />
    </Box>
  );
}
export default ActionsTable;
